import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'

// Pages
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import DashboardPage from './pages/DashboardPage'
import WorkspacePage from './editor/WorkspacePage'
import NotificationPage from './pages/NotificationPage'
import CreateTemplatePage from './pages/CreateTemplatePage'
import EditTemplatePage from './pages/EditTemplatePage'
import ImportFromLegacyPage from './pages/ImportFromLegacyPage'
import AppsPage from './pages/AppsPage'
import TrashPage from './pages/TrashPage'
import NotFoundPage from './pages/NotFoundPage'
import ProfileSettingsPage from './pages/ProfileSettingsPage'
import BackgroundRemovalPage from './pages/BackgroundRemovalPage'
import ToolsLandingPage from './pages/ToolsLandingPage'
import MiniPhotoShopPage from './editor/image-editor/ImageEditorPage'

// Dashboard Pages
import DashboardLayout from './components/Dashboard/DashboardLayout'
import DashboardHomePage from './pages/dashboard/DashboardHomePage'
import TemplatesPage from './pages/dashboard/TemplatesPage'
import MyTemplatesPage from './pages/dashboard/MyTemplatesPage'
import AllTemplatesPage from './pages/dashboard/AllTemplatesPage'
import CategoriesPage from './pages/dashboard/CategoriesPage'
import CalendarPage from './pages/Calendar/CalendarPage'
import PhotosPage from './pages/dashboard/PhotosPage'
import GraphicsPage from './pages/dashboard/GraphicsPage'
import AudioPage from './pages/dashboard/AudioPage'
import VideosPage from './pages/dashboard/VideosPage'
import TenantsPage from './pages/dashboard/TenantsPage'
import UsersPage from './pages/dashboard/UsersPage'
import TemplateAssignmentsPage from './pages/dashboard/TemplateAssignmentsPage'
import TenantWatermarkTemplatesPage from './pages/dashboard/TenantWatermarkTemplatesPage'
import ManageTemplatesPage from './pages/dashboard/ManageTemplatesPage'
import SampleValuesSetPage from './pages/dashboard/SampleValuesSetPage'
import FontPreviewPage from './pages/dashboard/FontPreviewPage'
import MultiTemplateGeneratorPage from './pages/dashboard/MultiTemplateGeneratorPage'
import MultiTemplateGeneratorFormPage from './pages/dashboard/MultiTemplateGeneratorFormPage'
import DeveloperApiPage from './pages/dashboard/DeveloperApiPage'
import AIGeneratorPage from './pages/dashboard/AIGeneratorPage'
import AIGeneratorFormPage from './pages/dashboard/AIGeneratorFormPage'
import AIExecutorPage from './pages/dashboard/AIExecutorPage'

// Components
import Layout from './components/Layout'
import Snackbar from './components/Notifications/Snackbar'
import AdminAccessDialog from './components/AdminAccessDialog'
import ProtectedRoute from './components/ProtectedRoute'
import { injectManyFonts } from './utils/fontLoader'

// Context
import { AuthProvider } from './contexts/AuthContext'

// Development config
import { isDevelopmentMode } from './config/development'
import CategoryManagement from './components/CategoryManagement'
import TenantManagement from './components/TenantManagement'
import PartialTextStylingDemo from './components/PartialTextStylingDemo'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  // 🚧 DEVELOPMENT MODE: Skip authentication for faster development
  React.useEffect(() => {
    // Load fonts from API (fonts_list.json with CDN URLs)
    import('./services/api').then(({ catalogAPI }) => {
      catalogAPI.getFonts()
        .then(({ data }) => {
          if (data?.success && Array.isArray(data.data?.fonts)) {
            // Inject first 200 fonts for faster initial load
            // Remaining fonts will be loaded on-demand when selected
            injectManyFonts(data.data.fonts, 200)
          }
        })
        .catch((error) => {
          console.warn('Failed to load fonts from API:', error)
        })
    })

    // Also try loading from fontList.js cache (for backward compatibility)
    import('./utils/fontList').then(({ getAvailableFonts }) => {
      getAvailableFonts().then((fonts) => {
        if (fonts && fonts.length > 0) {
          injectManyFonts(fonts, 100)
        }
      }).catch(() => {})
    })
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Snackbar />
          <Layout>
            <Routes>
              {/* Development: Redirect root to dashboard */}
              <Route
                path="/"
                element={
                  isDevelopmentMode() ?
                    <Navigate to="/dashboard" replace /> :
                    <HomePage />
                }
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              {/* Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardHomePage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="my-templates" element={<MyTemplatesPage />} />
                <Route path="all-templates" element={<AllTemplatesPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="photos" element={<PhotosPage />} />
                <Route path="graphics" element={<GraphicsPage />} />
                <Route path="audio" element={<AudioPage />} />
                <Route path="videos" element={<VideosPage />} />
                <Route path="tenants" element={<TenantsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="template-assignments" element={<TemplateAssignmentsPage />} />
                <Route path="tenant-watermark-templates" element={<TenantWatermarkTemplatesPage />} />
                <Route path="manage-templates" element={<ManageTemplatesPage />} />
                <Route path="sample-values-sets" element={<SampleValuesSetPage />} />
                <Route path="font-preview" element={<FontPreviewPage />} />
                <Route path="multi-template-generator" element={<MultiTemplateGeneratorPage />} />
                <Route path="multi-template-generator/create" element={<MultiTemplateGeneratorFormPage />} />
                <Route path="multi-template-generator/edit/:id" element={<MultiTemplateGeneratorFormPage />} />
                <Route path="ai-generator" element={<AIGeneratorPage />} />
                <Route path="ai-generator/create" element={<AIGeneratorFormPage />} />
                <Route path="ai-generator/:id" element={<AIGeneratorFormPage />} />
                <Route path="ai-generator/:generatorId/executors" element={<AIExecutorPage />} />
                <Route path="developer-api" element={<DeveloperApiPage />} />
              </Route>
              {/** category-management, category-fields and tenant-management */}
              <Route
                path="/category-management"
                element={
                  <ProtectedRoute>
                    <CategoryManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tenant-management"
                element={
                  <ProtectedRoute>
                    <TenantManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/text-styling-demo"
                element={
                  <ProtectedRoute>
                    <PartialTextStylingDemo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/create"
                element={
                  <ProtectedRoute>
                    <CreateTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/import-from-legacy"
                element={
                  <ProtectedRoute>
                    <ImportFromLegacyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates/edit/:id"
                element={
                  <ProtectedRoute>
                    <EditTemplatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editor/:template_id"
                element={
                  <ProtectedRoute>
                    <WorkspacePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/apps"
                element={
                  <ProtectedRoute>
                    <AppsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trash"
                element={
                  <ProtectedRoute>
                    <TrashPage />
                  </ProtectedRoute>
                }
              />
              {/* Public tools (no authentication required) */}
              <Route
                path="/tools"
                element={<ToolsLandingPage />}
              />
              <Route
                path="/tools/bgr"
                element={<BackgroundRemovalPage />}
              />
              {/* Legacy path -> redirect to the new tools route */}
              <Route
                path="/background-removal"
                element={<Navigate to="/tools/bgr" replace />}
              />
              <Route
                path="/mini-photoshop"
                element={
                  <ProtectedRoute>
                    <MiniPhotoShopPage />
                  </ProtectedRoute>
                }
              />
              {/* 404 Catch-all route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
        </Router>
        <Snackbar />
        <AdminAccessDialog />
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
